'use client'
import { useEffect, useRef } from 'react'

import { useInitNaverSdk } from '@/shared/hooks/useInitNaverSdk'

export interface ParkinglotMapViewModelProps {
  customLocationIcon: string
  lat: number
  lng: number
}

export interface ParkinglotMapViewModel {
  texts: object
  states: { naverMapRef: React.MutableRefObject<naver.maps.Map | null> }
  setters: object
  handlers: object
}

const useParkinglotMapViewModel = ({
  customLocationIcon,
  lat,
  lng
}: ParkinglotMapViewModelProps): ParkinglotMapViewModel => {
  const { isLoadScript } = useInitNaverSdk()
  const naverMapRef = useRef<naver.maps.Map | null>(null)
  const parkinglotMarkerRef = useRef<naver.maps.Marker | null>(null)

  const getNaverMap = ({ latitude, longitude }: { latitude: number; longitude: number }) => {
    const container = document.getElementById('map') as HTMLDivElement
    const center = new naver.maps.LatLng(latitude, longitude)
    const map: naver.maps.Map = new naver.maps.Map(container, {
      center,
      zoom: 19,
      minZoom: 0,
      maxZoom: 22,
      logoControlOptions: {
        position: naver.maps.Position.BOTTOM_LEFT
      },
      mapDataControl: false,
      scaleControl: false
    })
    return map
  }

  const generateParkinglotMarker = ({ latitude, longitude }: { latitude: number; longitude: number }) => {
    const parkinglotMarker = new naver.maps.Marker({
      position: new naver.maps.LatLng(latitude, longitude),
      map: naverMapRef.current!,
      icon: {
        content: customLocationIcon
      },
      zIndex: 100
    })
    return parkinglotMarker
  }

  const initMap = () => {
    const map = getNaverMap({ latitude: lat, longitude: lng })
    naverMapRef.current = map
  }

  useEffect(() => {
    if (window.naver && isLoadScript) {
      initMap()
      const parkinglotMarker = generateParkinglotMarker({ latitude: lat, longitude: lng })
      parkinglotMarkerRef.current = parkinglotMarker
      return () => {
        parkinglotMarkerRef.current!.setMap(null)
      }
    }
  }, [isLoadScript])

  return {
    texts: {},
    states: { naverMapRef },
    setters: {},
    handlers: {}
  }
}

export default useParkinglotMapViewModel
